import React, { useRef, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { cx } from '@/utils/strings'
import { createPortal } from 'react-dom'

interface SimpleTooltipProps {
  className?: string
  triggerClassName?: string
  content: React.ReactNode
  children: React.ReactNode
  backgroundColor?: 'Black' | 'White'
  /**
   * Whether the tooltip should be visible when hovering over the tooltip bubble element.
   * If false, the tooltip will remain visible only when hovering over the trigger itself
   *
   * Defaults to true.
   */
  isTooltipVisibleOnHover?: boolean
  /**
   * The max width of the tooltip.
   */
  maxWidth?: string
}

// Extra padding offset to trigger repositioning before the tooltip actually touches the viewport edge, to prevent jitter when hovering near edges
const VIEWPORT_PADDING_OFFSET = 16

export default function SimpleTooltip({
  content,
  children,
  className,
  triggerClassName,
  backgroundColor = 'White',
  isTooltipVisibleOnHover = true,
  maxWidth,
}: SimpleTooltipProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const tooltipPosition = useRef({ top: 0, left: 0 })
  const [position, setPosition] = useState({ horizontal: 'center', vertical: 'top' })

  // Detect touch device on mount
  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0)
  }, [])

  const showTooltip = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect()
      tooltipPosition.current = { top: rect.y, left: rect.x + rect.width / 2 }
      setIsVisible(true)
    }
  }

  const hideTooltip = () => {
    setIsVisible(false)
  }

  const toggleTooltip = () => {
    if (isVisible) {
      hideTooltip()
    } else {
      showTooltip()
    }
  }

  const adjustPosition = () => {
    if (!tooltipRef.current || !ref.current) return

    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    const triggerRect = ref.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const gap = 8 // gap between tooltip and trigger

    // Desktop positioning logic
    // Calculate ideal centered position
    const idealLeft = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
    const idealRight = idealLeft + tooltipRect.width

    // Check horizontal positioning
    let horizontal = 'center'
    let left = triggerRect.left + triggerRect.width / 2

    if (idealLeft < VIEWPORT_PADDING_OFFSET) {
      // Tooltip would overflow left
      horizontal = 'left'
      left = triggerRect.left + VIEWPORT_PADDING_OFFSET
    } else if (idealRight > viewportWidth - VIEWPORT_PADDING_OFFSET) {
      // Tooltip would overflow right
      horizontal = 'right'
      left = triggerRect.right - VIEWPORT_PADDING_OFFSET
    }

    // Check vertical positioning
    const spaceAbove = triggerRect.top
    const spaceBelow = viewportHeight - triggerRect.bottom
    const tooltipHeight = tooltipRect.height + gap

    let vertical = 'top'
    let top = triggerRect.top

    if (tooltipHeight > spaceAbove && spaceBelow > spaceAbove) {
      // Not enough space above and more space below
      vertical = 'bottom'
      top = triggerRect.bottom
    }

    tooltipPosition.current = { top, left }
    setPosition({ horizontal, vertical })
  }

  // Click outside to dismiss (especially important for mobile)
  useEffect(() => {
    if (!isVisible) return

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        tooltipRef.current &&
        ref.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        !ref.current.contains(event.target as Node)
      ) {
        hideTooltip()
      }
    }

    // Use timeout to avoid immediate dismissal on touch devices
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [isVisible])

  useEffect(() => {
    if (isVisible) {
      adjustPosition()

      // Add scroll listener to update position while scrolling
      const handleScroll = () => {
        adjustPosition()
      }

      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', adjustPosition)

      return () => {
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', adjustPosition)
      }
    }
  }, [isVisible])

  const getTransformClasses = () => {
    const classes = []

    if (position.vertical === 'top') {
      classes.push('-translate-y-full')
    }

    if (position.horizontal === 'center') {
      classes.push('-translate-x-1/2')
    } else if (position.horizontal === 'right') {
      classes.push('-translate-x-full')
    }

    return classes.join(' ')
  }

  const getArrowClasses = () => {
    const baseClasses = 'absolute w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent'
    const colorClasses =
      backgroundColor === 'Black'
        ? 'border-t-secondary-900 dark:border-t-white border-b-secondary-900 dark:border-b-white'
        : 'border-t-white dark:border-t-secondary-900 border-b-white dark:border-b-secondary-900'

    let positionClasses = ''

    if (position.vertical === 'top') {
      positionClasses = 'top-full border-t-8'
    } else {
      positionClasses = 'bottom-full border-b-8 border-t-0'
    }

    if (position.horizontal === 'center') {
      positionClasses += ' left-1/2 -translate-x-1/2'
    } else if (position.horizontal === 'left') {
      positionClasses += ' left-4'
    } else {
      positionClasses += ' right-4'
    }

    return cx(baseClasses, positionClasses, colorClasses)
  }

  const getMotionClasses = () => {
    return position.vertical === 'top' ? 'mb-2' : 'mt-2'
  }

  const getTooltipWidth = () => {
    const padding = VIEWPORT_PADDING_OFFSET

    // On desktop, calculate based on horizontal alignment
    let calculatedMax
    if (position.horizontal === 'left') {
      calculatedMax = `calc(100vw - ${tooltipPosition.current.left + padding * 2}px)`
    } else if (position.horizontal === 'right') {
      calculatedMax = `calc(${tooltipPosition.current.left - padding * 2}px)`
    } else {
      calculatedMax = `calc(100vw - ${padding * 2}px)`
    }

    return {
      width: 'max-content',
      maxWidth: calculatedMax,
    }
  }

  const tooltipWidth = getTooltipWidth()

  const handleMouseEnter = () => {
    if (!isTouchDevice) {
      showTooltip()
    }
  }

  const handleMouseLeave = () => {
    if (!isTouchDevice) {
      hideTooltip()
    }
  }

  const handleTouch = (e: React.TouchEvent) => {
    e.stopPropagation()
    toggleTooltip()
  }

  return (
    <span
      ref={ref}
      className={cx('inline-block', triggerClassName)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouch}
    >
      {children}
      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            className={cx('fixed z-100', getTransformClasses(), !isTooltipVisibleOnHover && 'pointer-events-none')}
            style={{ top: tooltipPosition.current.top, left: tooltipPosition.current.left }}
            onMouseEnter={() => isTooltipVisibleOnHover && !isTouchDevice && setIsVisible(true)}
            onMouseLeave={() => isTooltipVisibleOnHover && !isTouchDevice && hideTooltip()}
          >
            <motion.div
              className={getMotionClasses()}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ ease: 'anticipate', duration: 0.2 }}
            >
              <div
                className={cx(
                  'relative rounded-lg text-center md:text-left text-balance',
                  'px-4 py-3 sm:px-6 sm:py-4',
                  backgroundColor === 'Black' && 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900',
                  backgroundColor === 'White' &&
                    'bg-white text-black shadow-lg border-t-[1px] border-secondary-50 dark:bg-secondary-900 dark:text-white dark:border-secondary-700',
                  className
                )}
                style={{
                  width: tooltipWidth.width,
                  maxWidth: maxWidth ? `min(${maxWidth}, ${tooltipWidth.maxWidth})` : tooltipWidth.maxWidth,
                }}
              >
                {content}
                {/* This is a small amount of text */}
                <div className={getArrowClasses()} />
              </div>
            </motion.div>
          </div>,
          document.body
        )}
    </span>
  )
}
